import {useEffect, useState} from "react";
import Uploader from "../ui/components/upload/UploaderLib";
import { convertToJpeg, detectConvertibleImage, ImageConvertError, IMAGE_CONVERT_ERROR } from "../libs/imageConvert";
import { getUploadErrorMessage } from "../libs/uploadError";
import { signalerEchecConversion } from "../libs/uploadDiagnostic"; // TEMPORAIRE (28/09/2026)

function onUploadError({response}){
    let message = '';
    switch (response.statusCode) {
        case 413:
            message = `Fichier trop lourd, maximum accepté : ${response.errors.maxFileSizeStr}`;
            break;
        case 415:
            message = `Fichier non accepté, sont acceptés : ${response.errors.allowedTypes.join(', ')}`;
            break;
        default :
            message = 'Envoi du fichier impossible, erreur système inconnue';
            break;
    };
    return message;
}

const optionsDefault = {
    onProgress : (file,progress) => {},
    onSuccess : (data) =>{},
    onError : (file, error) =>{},
    onCancel : (file) =>{},
    isUploadMultiple : false
}

const uploadProgress = {
    loaded: 0,
    total: 0,
    timeStamp: 0,
    percentCompleted: 0,
    lastEtas : 0,
    lastKos : 0,
    eta: 0,
    kos: 0
}


const useUpload = (
    uploadPath = undefined,
    options = {}
) => {
    options = {
        ...optionsDefault,
        ...options
    };

    const [isInitialized, setIsInitialized] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);
    const [isError, setIsError] = useState(false);
    const [data, setData] = useState({});
    const [totalError, setTotalError] = useState(0);
    const [totalSuccess, setTotalSuccess] = useState(0);
    const [totalLoading, setTotalLoading] = useState(0);
    const [totalCancel, setTotalCancel] = useState(0);

    function _updateUploadProgress(file, progress){
        file.uploadProgress = progress;
        setData((data) => ({ ...data, [file.ephemeralId] : file }));
        options.onProgress(file, progress);
    }

    function initFileFormat(file){
        file.preview = "";
        file.uploadProgress = uploadProgress;
        file.isLoading = false;
        file.isSuccess = false;
        file.isError = false;
        file.isCancel = false;
        file.error = "";
        file.res = {};
        file.cancel = () =>{};
        return file;
    }

    async function _upload (selected){
        // HEIC ou WEBP, refusés par l'API : convertis en JPEG avant l'envoi, métadonnées recopiées
        // (cf. libs/imageConvert.js), comme pour les publications et les stories. Ce hook sert aux
        // images du forum (sujets, réponses), des salons, des articles, des signalements et de la
        // couverture vidéo : des WEBP y étaient refusés en 415 (forum, 15/10/2026).
        let fileData = selected;
        let conversion = null;
        let detected = { format: null, animated: false };
        try {
            detected = await detectConvertibleImage(selected);
            if (detected.format) {
                if (detected.animated) throw new ImageConvertError(IMAGE_CONVERT_ERROR.ANIMATED);
                fileData = await convertToJpeg(selected, detected.format);
                conversion = fileData.__conversion;
            }
        } catch (error) {
            if (error instanceof ImageConvertError) {
                const failed = initFileFormat(selected);
                failed.ephemeralId = failed.ephemeralId || Date.now().toString(36);
                failed.isError = true;
                failed.error = getUploadErrorMessage(error);
                setIsError(true);
                setTotalError(count => count + 1);
                setData((data) => ({ ...data, [failed.ephemeralId] : failed }));
                signalerEchecConversion({ file: selected, error, format: detected.format });
                options.onError(failed, error);
                return;
            }
            // Fichier illisible pour la détection : on garde l'original, comme avant.
        }

        const uploader = new Uploader({uploadPath});
        let {file, preview} =  await uploader.load(fileData);
        file = initFileFormat(file);
        file.preview = preview;
        file.isLoading = true;
        file.cancel = uploader.source.cancel;
        setData((data) => ({ ...data, [file.ephemeralId] : file }));
        setTotalLoading(count => count +1);
        let fileSendApi = new File([file], file.name, {type : file.type, lastModified : file.lastModified});
        fileSendApi.ephemeralId = file.ephemeralId;

        try {
            // Photo convertie : le compte rendu (original, JPEG produit, métadonnées) part dans
            // `jsonData`, l'API le journalise (« Image convertie côté client »).
            let res = await uploader.upload(conversion ? { conversion } : fileSendApi, (progress) => {_updateUploadProgress(file, progress)} );
            if(res){
                file.isSuccess = true;
                file.isLoading = false;
                file.res = res;

                setData((data) => ({ ...data, [file.ephemeralId] : file }));
                setTotalSuccess((count => count +1));
                options.onSuccess(file);
            }
            else{
                setTotalError((count => count +1));
                file.isError = true;
                file.error = "aucune reponse";
                setData((data) => ({ ...data, [file.ephemeralId] : file }));
            }
            setTotalLoading(count => count - 1);
        }
        catch (e) {
            file.isError = true;
            setIsError(true);
            if(e.response){
                let errorMessageType = onUploadError(e);
                file.error = errorMessageType;
                setTotalError((count => count +1));
                setTotalLoading(count => count - 1);
            }
            else{
                file.error = e.message;
            }
            options.onError(file, e);
        }
    }


    async function uploads (dataFiles){
        setIsInitialized(true);
        setIsSuccess(false);
        let files = Object.values(dataFiles);
        if(options.isUploadMultiple){
            let fileUploads = await Promise.all(files.map(async file =>{
                await _upload(file);
                return file;
            }));
            if(totalError > 0){
                setIsError(true);
            }
            if(totalError === 0 && totalSuccess === [...fileUploads].filter(item => !item.isCancel).length){
                setIsSuccess(true);
            }
        }
        else if(files.length){
            reset();
            await _upload(files[0]);
            if(totalError > 0){
                setIsError(true);
            }
            if(totalError === 0 && totalSuccess === 1){
                setIsSuccess(true);
            }
        }
        else{
            setIsError(true);
        }

    }

    function cancelUploadFile(ephemeralId){
        let file = data[ephemeralId];
        if(file && file.isLoading){
            file.cancel();
            setTotalCancel(count => count +1);
        }

        if(file.isSuccess){
            setTotalSuccess(count => count -1);
        }
        file = initFileFormat(file);
        file.isCancel = true;

        options.onCancel(file);
        setData((data) => ({ ...data, [file.ephemeralId] : file }));
    }


    function cancelAllUploadFiles() {
        let files = Object.values(data).filter(item => item.isLoading);
        files.forEach(file =>{
            cancelUploadFile(file.ephemeralId);
        });
    }

    function reset() {
        cancelAllUploadFiles();
        setIsInitialized(false);
        setIsSuccess(false);
        setIsError(false);
        setData({});
        setTotalError(0);
        setTotalSuccess(0);
        setTotalLoading(0);
        setTotalCancel(0);
    }

    let dataArray = Object.values(data);
    let dataReturn = {};
    if(options.isUploadMultiple){
       dataReturn = dataArray.length ? dataArray : [];
    }
    else{
        dataReturn = dataArray.length ? dataArray[0] : initFileFormat({});
    }

    useEffect(() => {
        return () =>{
            cancelAllUploadFiles();
        }
        // eslint-disable-next-line
    }, []);

    return {
        isSuccess,
        isError,
        isInitialized,
        totalLoading,
        totalSuccess,
        totalError,
        totalCancel,
        data : dataReturn,
        uploads,
        cancelUploadFile,
        cancelAllUploadFiles,
        resetUpload : reset
    };
}

export default useUpload;